import { useForm } from "@tanstack/react-form";
import type { AnyFieldApi } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { resetPassword } from "#/lib/auth-client";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";

const searchSchema = z.object({
  token: z.string().min(1),
  email: z.string().email(),
});

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirm: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

const AUTH_FIELD_CLASS = "h-14 px-6";

function fieldErrorText(field: AnyFieldApi): string | null {
  if (!field.state.meta.isTouched) return null;
  const errs = field.state.meta.errors;
  if (errs.length === 0) return null;
  return errs
    .map((e) => (typeof e === "string" ? e : (e?.message ?? "Invalid")))
    .join(", ");
}

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Reset Password" }] }),
  component: ResetPasswordPage,
  errorComponent: InvalidResetLink,
});

function InvalidResetLink() {
  return (
    <div data-screen-label="Reset Password" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24 text-center">
        <h1 className="m-0 mb-3 font-bold font-display text-3xl text-lp-green-dk leading-none tracking-tight">
          Reset link invalid
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          This password reset link is missing or expired. Request a new one to
          continue.
        </p>
        <Link
          to={ROUTES.forgotPassword}
          className="font-display text-lp-green-dk underline hover:text-lp-green-mid"
        >
          Request a new reset link
        </Link>
      </section>
    </div>
  );
}

function ResetPasswordPage() {
  const { token, email } = Route.useSearch();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: ResetPasswordValues) =>
      resetPassword({ email, password: values.password, token }),
    onSuccess: () => {
      notify.success("Password updated. Please sign in.");
      navigate({ to: ROUTES.login });
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Could not update password. The reset link may have expired.",
      );
    },
  });

  const form = useForm({
    defaultValues: { password: "", confirm: "" } satisfies ResetPasswordValues,
    validators: { onChange: resetPasswordSchema },
    onSubmit: ({ value }) => {
      setErrorMessage(null);
      return mutation.mutateAsync(value);
    },
  });

  return (
    <div data-screen-label="Reset Password" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Reset Password
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          Choose a new password for <strong>{email}</strong>.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-5"
          noValidate
        >
          <form.Field name="password">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="New Password">
                    {(id) => (
                      <Input
                        id={id}
                        type="password"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="new-password"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="confirm">
            {(field) => {
              const errText = fieldErrorText(field);
              return (
                <div>
                  <Field label="Confirm Password">
                    {(id) => (
                      <Input
                        id={id}
                        type="password"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="new-password"
                        aria-invalid={errText !== null}
                        className={AUTH_FIELD_CLASS}
                      />
                    )}
                  </Field>
                  {errText && (
                    <p
                      role="alert"
                      className="mt-1 font-display text-destructive text-sm"
                    >
                      {errText}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          {errorMessage && (
            <div className="font-display text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <PillBtn
                  type="submit"
                  variant="green"
                  size="sm"
                  disabled={
                    !canSubmit || isSubmitting || mutation.isPending
                  }
                >
                  {mutation.isPending || isSubmitting
                    ? "Updating…"
                    : "Update Password"}
                </PillBtn>
              )}
            </form.Subscribe>
          </div>
        </form>

        <div className="mt-10 font-display text-lp-text">
          <Link
            to={ROUTES.login}
            className="text-lp-green-dk underline hover:text-lp-green-mid"
          >
            Back to sign in
          </Link>
        </div>
      </section>
    </div>
  );
}
